<script>
import ImageUploader from '@lib/components/image-uploader'
import { mapActions, mapGetters, mapState } from 'vuex'
import amountMask from '@lib/utils/amount-mask'
import dropdownSorting from '@lib/utils/dropdown-sorting'
import get from 'lodash/get'

export default {
  name: 'ProviderDetailsEditModal',
  components: {
    ImageUploader,
  },
  props: {
    provider: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      processing: false,
      loaded: false,
      form: {
        id: null,
        name: null,
        image_uri: null,
        sales_tax_rate: null,
        category_tree_id: null,
        supplier_id: null,
      },
      rules: {
        name: {
          required: true,
          message: 'This field s required',
        },
        sales_tax_rate: {
          required: true,
          message: 'This field s required',
        },
        image_uri: {
          required: true,
          message: 'This field s required',
        },
        category_tree_id: {},
      },
    }
  },
  computed: {
    ...mapState('categoriesTree', ['categoriesTree']),
    ...mapState('suppliers', ['suppliers']),
    ...mapGetters('me', ['canSearchSupplier']),
    isEdit() {
      return !!get(this.provider, 'id')
    },
  },
  async created() {
    if (this.isEdit) {
      this.form = {
        id: this.provider.id,
        name: this.provider.name,
        image_uri: this.provider.image_uri,
        sales_tax_rate: this.provider.sales_tax_rate,
        category_tree_id: this.provider.category_tree_id || null,
        supplier_id: this.provider.supplier_id,
      }
    }

    await this.getCategoriesTree()

    if (this.canSearchSupplier) {
      await this.getSuppliers()
    }
    this.loaded = true
  },
  methods: {
    ...mapActions('providers', ['createProvider', 'updateProvider']),
    ...mapActions('categoriesTree', ['getCategoriesTree']),
    ...mapActions('suppliers', ['getSuppliers']),
    amountMask,
    dropdownSorting,
    onUploaded({ cdnUrl }) {
      this.form.image_uri = `https:${cdnUrl}`
    },
    async onSubmit() {
      const formValid = await this.$refs.form.validate().catch(() => false)
      if (!formValid) return

      this.processing = true
      const response = await this[this.isEdit ? 'updateProvider' : 'createProvider'](this.form)
      this.processing = false

      this.$notifier({
        response,
        successMsg: this.isEdit ? 'Changes successfully saved' : 'Provider successfully created',
      }).then(success => {
        if (success) {
          this.$emit('update:visible', false)
          this.$modal.self.hide()
          if (this.isEdit) {
            this.$emit('updated')
          } else {
            this.$router.push({
              name: 'provider-details',
              params: { id: success.id },
            })
          }
        }
      })
    },
  },
}
</script>

<template>
  <base-modal
    :title="isEdit ? 'Edit Provider Details' : 'Create Provider'"
    :check-form-changes="form"
  >
    <base-loader v-if="!loaded" />
    <el-form
      v-else
      ref="form"
      class="modal-form"
      :model="form"
      :rules="rules"
      label-position="top"
    >
      <div class="united-field">
        <el-form-item
          label="Provider Name"
          prop="name"
        >
          <el-input
            v-model="form.name"
            :disabled="isEdit"
          />
        </el-form-item>

        <el-form-item
          label="Tax Rate"
          prop="sales_tax_rate"
        >
          <el-input
            :value="form.sales_tax_rate"
            @input="form.sales_tax_rate = amountMask($event)"
          >
            <template
              #append
            >
              %
            </template>
          </el-input>
        </el-form-item>
      </div>
      <div>
        <el-form-item
          label="Supplier ID"
          prop="supplier_id"
        >
          <el-select
            v-if="canSearchSupplier"
            v-model="form.supplier_id"
          >
            <el-option
              label="Not Selected"
              :value="null"
            />
            <el-option
              v-for="supplier in dropdownSorting(suppliers, 'name')"
              :key="supplier.id"
              :value="supplier.id"
              :label="supplier.name"
            />
          </el-select>
          <el-input
            v-else
            v-model="form.supplier_id"
          />
        </el-form-item>
      </div>

      <div>
        <el-form-item
          label="Logo URL"
          prop="image_uri"
          :class="$style.uploaderContainer"
        >
          <image-uploader
            :image="form.image_uri"
            :class="$style.uploader"
            @onUpload="onUploaded($event)"
          >
            <span slot="button-text">
              Change Logo
            </span>
          </image-uploader>
        </el-form-item>
      </div>

      <div>
        <el-form-item
          label="Category Tree"
          prop="category_tree_id"
        >
          <el-select v-model="form.category_tree_id">
            <el-option
              label="Not Selected"
              :value="null"
            />
            <el-option
              v-for="tree in dropdownSorting(categoriesTree, 'name')"
              :key="tree.id"
              :value="tree.id"
              :label="tree.name"
            />
          </el-select>
        </el-form-item>
      </div>

      <el-button
        type="primary"
        :class="$style.button"
        :loading="processing"
        :disabled="processing"
        class="el-button--wide"
        @click.stop="onSubmit"
      >
        Save
      </el-button>
    </el-form>
  </base-modal>
</template>

<style lang="scss" module>
.button {
  display: block;
  margin-right: auto;
  margin-left: auto;
}

.uploaderContainer {
  max-width: rem(235px);
}

.uploader {
  justify-content: flex-start;
  padding: 0;
  border: none;

  :global {
    .el-button {
      position: relative !important;
    }
  }
}
</style>
